/**
 * Gemeinsamer Kern von `IwmFormContainer.razor` und `IwmFormRechnung.razor`: View laden,
 * Felder nachschlagen, Datensatz laden und speichern. Beide Formulare unterscheiden sich
 * darüber hinaus nur in ihrer Feldliste.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ViewApi } from '@/api/apiViewBase';
import { viewApi } from '@/api/services/viewApi';
import { useAuth } from '@/auth/useAuth';
import { useDialogService } from '@/components/ui';
import type { BaseEntity, ChinaAppSysView, ChinaAppSysViewField } from '@/types/entities';
import { ValidationStatusTypes, type EntityTypes } from '@/types/enums';

import type { FieldAccess } from './fields/fieldAccess';
import { requiredMessages, type RequiredRule } from './requiredFields';
import { useEntityForm } from './useEntityForm';

export interface ViewFormOptions<T extends BaseEntity> {
  id: number;
  entity: EntityTypes;
  api: ViewApi<T>;
  createEmpty: () => T;
  rules: RequiredRule<T>[];
  onSave: (id: number) => void;
}

export interface ViewForm<T> extends FieldAccess<T> {
  /** Renderbedingung aus REQ-10 §3: `viewId != null && viewId > 0 && model != null`. */
  ready: boolean;
  saving: boolean;
  isValid: boolean;
  /** Pendant zu `Fetch()`: lädt View **und** Datensatz neu. */
  reload: () => void;
  save: () => Promise<void>;
  /** Der geladene Datensatz — die Schublade bildet daraus ihren Titel (REQ-27 §2.2). */
  model: T;
  /** View der Entität; `0`, solange keine ermittelt ist. Lesende Zusatzabfragen brauchen sie. */
  viewId: number;
}

export function useViewForm<T extends BaseEntity>(options: ViewFormOptions<T>): ViewForm<T> {
  const { api, createEmpty, entity, id, onSave, rules } = options;
  // eslint-disable-next-line @typescript-eslint/unbound-method -- stabile Closure, keine Methode
  const { getViewIdByEntity } = useAuth();
  const dialogService = useDialogService();

  // `null` heißt „noch nicht geladen“ — bis dahin bleibt das Formular unsichtbar, so wie
  // Blazor erst nach `OnInitializedAsync` zeichnet.
  const [view, setView] = useState<{ fields: ChinaAppSysViewField[] } | null>(null);
  const viewId = getViewIdByEntity(entity);

  const loadView = useCallback(async (): Promise<void> => {
    // `APIBase` liefert im Fehlerfall `null` (REQ-00 §8).
    const loaded: ChinaAppSysView | null = await viewApi.getEntry(getViewIdByEntity(entity) ?? 0);
    setView({ fields: loaded?.Fields ?? [] });
  }, [entity, getViewIdByEntity]);

  useEffect(() => {
    void loadView();
  }, [loadView]);

  const form = useEntityForm<T>({
    id,
    createEmpty,
    fetch: (entryId) =>
      viewId !== null && viewId > 0 ? api.getEntry(viewId, entryId) : Promise.resolve(null),
    validate: (model) => [...requiredMessages(model, rules).values()],
  });
  // eslint-disable-next-line @typescript-eslint/unbound-method -- stabile Closures, keine Methoden
  const { isValid, model, reload: reloadEntity, setField, setModel, setSaving } = form;

  const messages = useMemo(() => requiredMessages(model, rules), [model, rules]);

  const save = useCallback(async (): Promise<void> => {
    if (!isValid) return;

    const targetView = viewId ?? 0;
    const status = await api.validate(targetView, model);
    if (status.Status === ValidationStatusTypes.INVALID) {
      await dialogService.alert(status.Message, 'Validierungsfehler');
      return;
    }

    setSaving(true);
    const currentId = model.Id;
    let saved = model;
    if (currentId > 0) {
      await api.put(targetView, currentId, model);
    } else {
      saved = await api.create(targetView, model);
      setModel(saved);
    }
    setSaving(false);
    onSave(currentId === 0 ? saved.Id : currentId);
  }, [api, dialogService, isValid, model, onSave, setModel, setSaving, viewId]);

  const reload = useCallback(() => {
    void loadView();
    void reloadEntity();
  }, [loadView, reloadEntity]);

  return useMemo(
    () => ({
      ready: viewId !== null && viewId > 0 && view !== null,
      saving: form.saving,
      isValid,
      reload,
      save,
      model,
      viewId: viewId ?? 0,
      value: (name) => model[name],
      setValue: (name, next) => {
        // Die Bausteine liefern stets den Typ des Modellfeldes (Pendant zu `@bind-Value`).
        setField(name, next as T[keyof T & string]);
      },
      viewField: (name) =>
        view?.fields.find((field) => field.FieldName?.toLowerCase() === name.toLowerCase()) ?? null,
      message: (name) => messages.get(name),
    }),
    [form.saving, isValid, messages, model, reload, save, setField, view, viewId],
  );
}
